import { HireWizard } from "@/pages/employees/hire/HirePage";
import { useHiringFlow, useHiringFlowUpdate } from "@/queries/use-hiring-flows.query";
import { useUnsavedChangesBlocker } from "@/hooks/use-unsaved-changes-blocker.hook";
import {
  ApiHiringFlow,
  ApiHiringFlowUpdate,
  HiringFlowTab,
  HiringFlowEditorSection,
} from "@/types/hiring-flow.types";
import {
  Button,
  FontAwesomeIcon,
  Input,
  Page,
  Skeleton,
  Stack,
  TabItem,
  Tabs,
  Text,
  useToasts,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { HiringFlowFields } from "@/pages/settings/hiring-flows/edit/components/HiringFlowFields";
import { HiringFlowOnboarding } from "@/pages/settings/hiring-flows/edit/components/HiringFlowOnboarding";

function getEditableState(settings: ApiHiringFlow, form: ApiHiringFlow["form"]) {
  const { id, owner, datetime_updated_at, form: savedForm, ...values } = settings;
  void id;
  void owner;
  void datetime_updated_at;
  void savedForm;
  const mapSections = (sections: ApiHiringFlow["form"][HiringFlowTab.main]) =>
    sections.map((section) => ({
      section_type: section.section_type,
      title: section.title,
      fields: section.fields.map((field) => ({
        slug: field.field.slug,
        is_enabled: field.is_enabled,
        is_mandatory: field.is_mandatory,
      })),
    }));
  return JSON.stringify({
    ...values,
    form: { main: mapSections(form.main), personal: mapSections(form.personal) },
  });
}

export function HiringFlowEditPage() {
  const { id } = useParams();
  const { data, isLoading, isError } = useHiringFlow(Number(id));
  const { t } = useTranslation();
  if (isLoading || !data || isError) {
    return (
      <Page>
        <Page.Header
          showBack
          navigateTo="/settings/hiring-flows"
          titleSlot={isLoading ? <Skeleton className="h-6 w-48" /> : undefined}
        />
        <Page.Content>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <Text>{t("hiring_flow:not_found")}</Text>
          )}
        </Page.Content>
      </Page>
    );
  }
  return <HiringFlowEditor key={data.id} flow={data} />;
}

function HiringFlowEditor({ flow }: { flow: ApiHiringFlow }) {
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const update = useHiringFlowUpdate();
  const [settings, setSettings] = useState(flow);
  const [preview, setPreview] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [savedState, setSavedState] = useState(() => getEditableState(flow, flow.form));
  const [form, setForm] = useState<
    Record<HiringFlowTab.main | HiringFlowTab.personal, HiringFlowEditorSection[]>
  >(() => ({
    main: flow.form.main.map((section, index) => ({ ...section, key: `main-${index}` })),
    personal: flow.form.personal.map((section, index) => ({
      ...section,
      key: `personal-${index}`,
    })),
  }));
  const hasUnsavedChanges = getEditableState(settings, form) !== savedState;
  useUnsavedChangesBlocker(hasUnsavedChanges);

  const save = async () => {
    setSubmitted(true);
    if (!settings.title.trim()) return;
    const mapSections = (sections: HiringFlowEditorSection[]) =>
      sections.map((section) => ({
        section_type: section.section_type,
        title: section.title,
        fields: section.fields.map((field) => ({
          slug: field.field.slug,
          is_enabled: field.is_enabled,
          is_mandatory: field.is_mandatory,
        })),
      }));
    const { id, owner, datetime_updated_at, form: oldForm, ...payloadSettings } = settings;
    void owner;
    void datetime_updated_at;
    void oldForm;
    const payload: ApiHiringFlowUpdate = {
      ...payloadSettings,
      title: settings.title.trim(),
      form: { main: mapSections(form.main), personal: mapSections(form.personal) },
    };
    try {
      const result = await update.mutateAsync({ id, payload });
      setSavedState(getEditableState(result, result.form));
      setSettings(result);
      setForm({
        main: result.form.main.map((section, index) => ({ ...section, key: `main-${index}` })),
        personal: result.form.personal.map((section, index) => ({
          ...section,
          key: `personal-${index}`,
        })),
      });
      showInfo({ title: t("hiring_flow:saved") });
    } catch {
      return;
    }
  };
  const disabled = update.isPending;
  if (preview)
    return (
      <div className="fixed inset-0 z-40">
        <HireWizard
          flow={{ ...settings, form }}
          isPreview
          onExitPreview={() => setPreview(false)}
        />
      </div>
    );
  return (
    <Page>
      <Page.Header title={settings.title} showBack navigateTo="/settings/hiring-flows">
        <Stack gap={"sm"} direction={"horizontal"}>
          <Button
            variant="secondary"
            onClick={() => setPreview(true)}
            disabled={disabled}
            rightIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />}
          >
            {t("hiring_flow:preview_form")}
          </Button>
          <Button onClick={save} isLoading={update.isPending}>
            {t("common:save")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content>
        <Stack gap="md" className={"px-16"}>
          <Input
            label={t("common:title")}
            value={settings.title}
            required
            disabled={update.isPending}
            onChange={(e) => setSettings({ ...settings, title: e.target.value })}
            error={submitted && !settings.title.trim() ? t("errors:field_required") : undefined}
          />
          <Tabs>
            <TabItem title={t("hiring_flow:main")}>
              <HiringFlowFields
                sections={form.main}
                onChange={(main) => setForm({ ...form, main })}
                disabled={disabled}
              />
            </TabItem>
            <TabItem title={t("hiring_flow:personal")}>
              <HiringFlowFields
                sections={form.personal}
                onChange={(personal) => setForm({ ...form, personal })}
                disabled={disabled}
              />
            </TabItem>
            <TabItem title={t("hiring_flow:onboarding")}>
              <HiringFlowOnboarding
                value={settings}
                onChange={(changes) => setSettings({ ...settings, ...changes })}
                disabled={disabled}
              />
            </TabItem>
          </Tabs>
        </Stack>
      </Page.Content>
    </Page>
  );
}
